import { useState, useCallback, useRef, useEffect } from 'react';
import { getValidAccessToken, refreshToken } from '../auth/office-auth.service';
import { endpoints } from '../config/endpoints';
import { apiFetch } from '../api/client';
import { getHostToolsSync } from '../tools/office-tools-registry';
import { sharedTools } from '../tools/shared-tools';
import { getDocumentContext } from '../services/document-context';
import type { PendingQuestion } from '../tools/ask-user-tool';
import type { OfficeToolDefinition } from '../tools/office-tools-registry';
import type { ChatMessage, ChatMessageAttachment, AgentSSEEvent } from '../types/chat.types';
import type { AttachmentMeta } from './use-file-attachments';
import { uploadAttachmentsForPrompt, buildAttachmentPromptSection } from '../api/upload';
import { downloadSkillAsset, findSkillIdByName } from '../api/skills';
import { zodToJsonSchema } from 'zod-to-json-schema';
import { getToolDisplayName } from '../config/tool-display-config';
import { getErrorMessage } from '../utils/error';
import { addDatadogAction, addDatadogError } from '../services/datadog';
import { parseAttachmentMarkup } from '../utils/attachment-markup';

// ─── Skill Asset Resolution for generate_template_slide ──────────
const ASSET_MIME_MAP: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.svg': 'image/svg+xml',
  '.bmp': 'image/bmp',
};

function blobToDataUri(blob: Blob, filePath: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const ext = filePath.slice(filePath.lastIndexOf('.')).toLowerCase();
    const mime = ASSET_MIME_MAP[ext] || blob.type || 'image/png';
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(`data:${mime};base64,${base64}`);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Resolves "asset" fields in generate_template_slide tool args to base64 data URIs
 * by fetching them from the skill assets API endpoint.
 */
async function resolveTemplateSlideAssets(
  args: Record<string, unknown>,
  workspaceId: string,
): Promise<Record<string, unknown>> {
  const slide = args.slide as Record<string, unknown> | undefined;
  if (!slide) return args;

  const skillName = (args.skill_name ?? args.skillName ?? args.template_name) as string;
  if (!skillName) return args;

  // Check if there are any asset references
  const bg = slide.background;
  const elements = slide.elements as Record<string, unknown>[] | undefined;
  const hasBgAsset = bg && typeof bg === 'object' && bg !== null && 'asset' in (bg as Record<string, unknown>);
  const hasElementAssets = elements?.some(el => el.asset && typeof el.asset === 'string');
  if (!hasBgAsset && !hasElementAssets) return args;

  // Look up skill ID by name
  let skillId: string | null = null;
  try {
    skillId = await findSkillIdByName(workspaceId, skillName);
  } catch (err) {
    console.warn('[resolveTemplateSlideAssets] Failed to find skill:', err);
    return args;
  }
  if (!skillId) {
    console.warn(`[resolveTemplateSlideAssets] Skill "${skillName}" not found`);
    return args;
  }

  const resolvedSlide = { ...slide };

  // Resolve background asset
  if (hasBgAsset) {
    const bgObj = bg as Record<string, unknown>;
    const assetPath = bgObj.asset as string;
    try {
      const blob = await downloadSkillAsset(workspaceId, skillId, assetPath);
      const dataUri = await blobToDataUri(blob, assetPath);
      const elems = ((resolvedSlide.elements as Record<string, unknown>[]) || []).slice();
      elems.unshift({ type: 'image', data: dataUri, x: 0, y: 0, w: 13.33, h: 7.5 });
      resolvedSlide.elements = elems;
      delete resolvedSlide.background;
    } catch (err) {
      console.warn(`[resolveTemplateSlideAssets] Failed to resolve bg asset: ${assetPath}`, err);
    }
  }

  // Resolve element-level asset references
  const elems = resolvedSlide.elements as Record<string, unknown>[] | undefined;
  if (elems && Array.isArray(elems)) {
    resolvedSlide.elements = await Promise.all(
      elems.map(async (element): Promise<Record<string, unknown>> => {
        if (!element.asset || typeof element.asset !== 'string') return element;
        const assetPath = element.asset as string;
        try {
          const blob = await downloadSkillAsset(workspaceId, skillId!, assetPath);
          const dataUri = await blobToDataUri(blob, assetPath);
          const resolved: Record<string, unknown> = { ...element, data: dataUri };
          delete resolved.asset;
          if (!resolved.type) resolved.type = 'image';
          return resolved;
        } catch (err) {
          console.warn(`[resolveTemplateSlideAssets] Failed to resolve asset: ${assetPath}`, err);
          return element;
        }
      }),
    );
  }

  return { ...args, slide: resolvedSlide };
}

// ─── Chat State Machine ──────────────────────
export const ChatState = {
  IDLE: 'idle',
  SENDING: 'sending',
  STREAMING: 'streaming',
  REQUEST_IN_PROGRESS: 'request_in_progress',
  STOPPING: 'stopping',
  RECONNECTING: 'reconnecting',
} as const;
export type ChatState = (typeof ChatState)[keyof typeof ChatState];

// ─── Event History → Messages (for session loading) ──────────
// Modeled after hai_assistants processEventHistoryToMessages

interface EventHistoryEntry {
  eventId: string;
  eventType: string;
  eventData: Record<string, unknown>;
  direction: string;
  timestamp?: string;
  messageOrder?: number;
  userResponse?: { selectedValue?: string; feedback?: string };
}

/**
 * Adapts parseAttachmentMarkup (from hai_assistants/ui) to the ChatMessageAttachment shape.
 */
function parseAttachmentsFromContent(raw: string): { content: string; attachments?: ChatMessageAttachment[] } {
  const { textContent, attachments } = parseAttachmentMarkup(raw);
  const mapped = attachments.map(a => ({ fileId: a.path || a.filename, fileName: a.filename, fileSize: 0 }));
  return { content: textContent, attachments: mapped.length ? mapped : undefined };
}

function processEventHistoryToMessages(events: EventHistoryEntry[]): ChatMessage[] {
  if (!events || events.length === 0) return [];

  const messages: ChatMessage[] = [];

  const findLastToolMessage = (): ChatMessage | undefined => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].toolCallId && messages[i].toolName) return messages[i];
    }
    return undefined;
  };

  const findMatchingToolIndex = (toolCallId?: string, toolName?: string): number => {
    if (toolCallId) {
      const idx = messages.findIndex(m => m.toolCallId === toolCallId);
      if (idx >= 0) return idx;
    }
    if (toolName) {
      const candidates = messages
        .map((m, index) => ({ m, index }))
        .filter(({ m }) => m.toolName === toolName)
        .filter(({ m }) => !m.toolStatus || m.toolStatus === 'pending' || m.toolStatus === 'awaiting_approval');
      if (candidates.length === 1) return candidates[0].index;
    }
    return -1;
  };

  for (const event of events) {
    const { eventId, eventType, eventData, direction, userResponse } = event;
    const ts = event.timestamp ? new Date(event.timestamp).getTime() : Date.now();

    if (direction === 'inbound') {
      switch (eventType) {
        case 'INITIAL_USER_MESSAGE':
        case 'AGENT_ASK':
        case 'SYSTEM_ASK':
        case 'AGENT_EXECUTION_STARTED': {
          const raw = (userResponse?.feedback || eventData?.humanMessage || '') as string;
          if (raw) {
            const { content, attachments } = parseAttachmentsFromContent(raw);
            messages.push({ id: eventId, role: 'user', content, timestamp: ts, ...(attachments && { attachments }) });
          }
          break;
        }

        case 'TOOL_USE':
        case 'TOOL_POST_APPROVAL': {
          const isRejected = userResponse?.selectedValue === 'REJECT_WITH_FEEDBACK';
          if (isRejected) {
            const lastTool = findLastToolMessage();
            if (lastTool) {
              lastTool.toolStatus = 'error';
              lastTool.toolError = 'Rejected by user';
              lastTool.content = `${lastTool.toolName} rejected`;
            }
            if (userResponse?.feedback) {
              messages.push({ id: eventId, role: 'user', content: userResponse.feedback, timestamp: ts });
            }
          }
          break;
        }

        case 'EXTERNAL_TOOL_INVOKE':
          // Inbound = frontend's tool response — no visible message
          break;

        default: {
          const raw = (userResponse?.feedback || eventData?.humanMessage || '') as string;
          if (raw) {
            const { content, attachments } = parseAttachmentsFromContent(raw);
            messages.push({ id: eventId, role: 'user', content, timestamp: ts, ...(attachments && { attachments }) });
          }
          break;
        }
      }
    } else {
      // Outbound (from AI/system)
      switch (eventType) {
        case 'AGENT_RESPONSE_CHUNK': {
          const chunk = (eventData?.aiMessage ?? eventData?.content ?? eventData?.chunk ?? '') as string;
          if (!chunk) break;
          const lastMsg = messages[messages.length - 1];
          if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.toolCallId) {
            lastMsg.content += chunk;
          } else {
            messages.push({ id: eventId, role: 'assistant', content: chunk, timestamp: ts });
          }
          break;
        }

        case 'AGENT_RESPONSE':
        case 'RESPONSE': {
          const content = (eventData?.aiMessage ?? eventData?.content ?? '') as string;
          const lastMsg = messages[messages.length - 1];

          // If text was being streamed, mark as complete
          if (lastMsg && lastMsg.role === 'assistant' && !lastMsg.toolCallId) {
            if (content && !lastMsg.content) lastMsg.content = content;
          } else if (content) {
            messages.push({ id: eventId, role: 'assistant', content, timestamp: ts });
          }

          // AGENT_RESPONSE may carry toolDetail — create or update a tool message
          const toolDetail = eventData?.toolDetail as Record<string, unknown> | undefined;
          if (toolDetail) {
            const toolName = (toolDetail.name || '') as string;
            const toolCallId = (toolDetail.id || eventId) as string;
            const toolArgs = (toolDetail.args || {}) as Record<string, unknown>;
            const idx = findMatchingToolIndex(toolCallId, toolName);
            if (idx >= 0) {
              messages[idx] = {
                ...messages[idx],
                toolCallId: messages[idx].toolCallId ?? toolCallId,
                toolArgs: Object.keys(toolArgs).length > 0 ? toolArgs : messages[idx].toolArgs,
                toolStatus: 'pending',
              };
            } else {
              messages.push({
                id: crypto.randomUUID(), role: 'assistant',
                content: `Using ${toolName}...`, timestamp: ts,
                toolCallId, toolName, toolArgs, toolStatus: 'pending',
              });
            }
          }
          break;
        }

        case 'TOOL_RESPONSE': {
          const result = eventData?.aiMessage ?? eventData?.result ?? eventData?.output ?? '';
          const lastTool = findLastToolMessage();
          if (lastTool && lastTool.toolStatus !== 'success' && lastTool.toolStatus !== 'error') {
            lastTool.toolStatus = 'success';
            lastTool.toolResult = result;
            lastTool.content = `${lastTool.toolName} completed`;
          }
          break;
        }

        case 'TOOL_USE_ERROR': {
          const errMsg = (eventData?.aiMessage ?? eventData?.error ?? 'Tool error') as string;
          const lastTool = findLastToolMessage();
          if (lastTool && lastTool.toolStatus !== 'success' && lastTool.toolStatus !== 'error') {
            lastTool.toolStatus = 'error';
            lastTool.toolError = errMsg;
            lastTool.content = `${lastTool.toolName} failed`;
          }
          break;
        }

        case 'TOOL_USE':
        case 'TOOL_POST_APPROVAL': {
          // Outbound TOOL_USE — tool message may already exist from AGENT_RESPONSE's toolDetail
          const toolDetail = eventData?.toolDetail as Record<string, unknown> | undefined;
          if (toolDetail) {
            const toolName = (toolDetail.name || '') as string;
            const toolCallId = (toolDetail.id || eventId) as string;
            const toolArgs = (toolDetail.args || {}) as Record<string, unknown>;
            const idx = findMatchingToolIndex(toolCallId, toolName);
            if (idx >= 0) {
              messages[idx] = {
                ...messages[idx],
                toolCallId: messages[idx].toolCallId ?? toolCallId,
                toolArgs: Object.keys(toolArgs).length > 0 ? toolArgs : messages[idx].toolArgs,
                toolStatus: 'pending',
              };
            } else {
              messages.push({
                id: crypto.randomUUID(), role: 'assistant',
                content: `Using ${toolName}...`, timestamp: ts,
                toolCallId, toolName, toolArgs, toolStatus: 'pending',
              });
            }
          }
          break;
        }

        case 'EXTERNAL_TOOL_INVOKE': {
          // Skip creating a new tool message here; update existing if present.
          const toolDetail = eventData?.toolDetail as Record<string, unknown> | undefined;
          const toolName = (toolDetail?.name ?? eventData?.toolName ?? '') as string;
          const toolCallId = (toolDetail?.id ?? eventData?.toolCallId ?? eventId) as string;
          const toolArgs = (toolDetail?.args ?? eventData?.toolArgs ?? {}) as Record<string, unknown>;
          const idx = findMatchingToolIndex(toolCallId, toolName);
          if (idx >= 0) {
            messages[idx] = {
              ...messages[idx],
              toolCallId: messages[idx].toolCallId ?? toolCallId,
              toolArgs: Object.keys(toolArgs).length > 0 ? toolArgs : messages[idx].toolArgs,
              toolStatus: 'pending',
            };
          }
          break;
        }

        case 'AGENT_ASK': {
          const content = (eventData?.aiMessage ?? eventData?.content ?? '') as string;
          if (content && content !== '/exit') {
            messages.push({ id: eventId, role: 'assistant', content, timestamp: ts });
          }
          break;
        }

        // AGENT_ERROR, ERROR, USER_STOP, etc. — skip for history display
        default:
          break;
      }
    }
  }

  // Mark any tools still stuck in pending/running as cancelled.
  // This happens when the user closed the task pane mid-execution
  // and no TOOL_RESPONSE was ever recorded for the tool.
  // Skip tools that already have a result (they completed successfully
  // but the status wasn't updated before the pane closed).
  for (const msg of messages) {
    if (msg.toolCallId && msg.toolStatus === 'pending' && !msg.toolResult) {
      msg.toolStatus = 'error';
      msg.toolError = 'Interrupted — task pane was closed during execution';
      msg.content = `${msg.toolName || 'Tool'} interrupted`;
    } else if (msg.toolCallId && msg.toolStatus === 'pending' && msg.toolResult) {
      msg.toolStatus = 'success';
      msg.content = `${msg.toolName || 'Tool'} completed`;
    }
  }

  return messages;
}

export interface UseChatOptions {
  sessionId?: string;
  agentId?: string;
  workspaceId?: string;
  officeHost: string;
  tools: OfficeToolDefinition[];
  onSessionCreated?: (sessionId: string) => void;
  onChatStateChange?: (isActive: boolean) => void;
}

export function useChat(options: UseChatOptions) {
  const { sessionId: initialSessionId, agentId, workspaceId, officeHost, tools, onSessionCreated, onChatStateChange } = options;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatState, setChatState] = useState<ChatState>(ChatState.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>(initialSessionId);
  const [isArchived, setIsArchived] = useState(false);
  const [activeSkills, setActiveSkills] = useState<Array<{ name: string; description: string }>>([]);
  const [connectorStatuses, setConnectorStatuses] = useState<Array<{ id: number; name: string; status: string; oauth?: { status: string; authorizationUrl?: string } }>>([]);
  const [pendingQuestion, setPendingQuestion] = useState<PendingQuestion | null>(null);

  // Notify parent component when chat state changes (for confirmation dialogs)
  useEffect(() => {
    const isActive = chatState === ChatState.SENDING || chatState === ChatState.STREAMING || chatState === ChatState.STOPPING;
    onChatStateChange?.(isActive);
  }, [chatState, onChatStateChange]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const externalToolAbortRef = useRef<AbortController | null>(null);
  const externalToolRunningRef = useRef(false);
  const streamBufferRef = useRef<string | null>(null);
  const lastAssistantContentRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | undefined>(initialSessionId);
  const pendingApprovalRef = useRef<Map<string, { toolCallId: string; toolName: string; toolArgs: Record<string, unknown>; token: string }>>(new Map());
  const lastToolRejectedRef = useRef(false);
  // Track streaming tool calls (name, id, args chunks)
  const toolStreamBufferRef = useRef<{
    id: string;
    name: string;
    args: string;
    index: number;
  } | null>(null);
  // Keep tools in a ref so closures always see the latest value
  const toolsRef = useRef<OfficeToolDefinition[]>(tools);
  toolsRef.current = tools;

  // Cache serialized tools — zodToJsonSchema is expensive and tool definitions
  // don't change mid-session. Only recompute when the tool array reference changes.
  const serializedToolsCacheRef = useRef<{ source: OfficeToolDefinition[]; serialized: ReturnType<typeof serializeFrontendTools> } | null>(null);

  const serializeFrontendTools = (toolList: OfficeToolDefinition[]) => {
    return toolList.map((tool) => ({
      name: tool.name,
      description: tool.description,
      parameters: zodToJsonSchema(tool.parameters, {
        $refStrategy: 'none',
      }),
    }));
  };

  const getCachedSerializedTools = (toolList: OfficeToolDefinition[]) => {
    const cache = serializedToolsCacheRef.current;
    if (cache && cache.source === toolList) {
      return cache.serialized;
    }
    const serialized = serializeFrontendTools(toolList);
    serializedToolsCacheRef.current = { source: toolList, serialized };
    return serialized;
  };

  // Derived loading state for backward compatibility
  const isLoading = chatState !== ChatState.IDLE;

  // ask_user_question bridge removed — tool not used in Office Add-in

  /** Resolve a tool by name — checks the ref first, then sync cached registry. */
  const resolveTool = (toolName: string): OfficeToolDefinition | undefined => {
    // First check the memoized tools ref (fastest path)
    let tool = toolsRef.current.find(t => t.name === toolName);
    if (!tool) {
      // Ref may be stale (captured before Office.onReady). Use the sync cache
      // which was populated by ChatInterface's async loadHostTools() call.
      const cachedTools = getHostToolsSync();
      tool = cachedTools.find(t => t.name === toolName);
      if (cachedTools.length > toolsRef.current.length) {
        toolsRef.current = cachedTools;
      }
    }
    if (!tool) {
      console.warn(`[useChat] resolveTool: "${toolName}" not found. toolsRef has ${toolsRef.current.length} tools.`);
    }
    return tool;
  };

  /** Get a valid auth token (refreshes if near expiry). */
  const getToken = async (): Promise<string> => {
    const token = await getValidAccessToken();
    if (!token) throw new Error('Not authenticated');
    return token;
  };

  /** Build frontendContext with platform context and document state.
   *  Send tool definitions from the frontend so backend can use them directly. */
  const buildFrontendContext = async () => {
    let documentContext: Awaited<ReturnType<typeof getDocumentContext>> = null;
    try {
      documentContext = await getDocumentContext();
    } catch {
      // Context is optional
    }

    const serializedTools = getCachedSerializedTools(toolsRef.current);

    // Build platformContext so the backend knows this is an Office add-in
    // and can load the correct tool definitions server-side
    const platformContext: Record<string, unknown> = {
      platform: officeHost ? `office-${officeHost.toLowerCase()}` : 'web',
      deviceType: 'desktop' as const,
      ...(documentContext && { officeDocumentContext: documentContext }),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      currentDateTime: new Date().toISOString(),
      // Vision capability flag — derived from whether vision tools are in the tool list.
      // When the frontend filters out take_slide_screenshot (non-vision model), this is false.
      supportsVision: serializedTools.some((t) => t.name === 'take_slide_screenshot'),
    };

    return {
      tools: serializedTools,
      additionalConfig: {
        includeMcpTools: true,
        isMemoryEnabled: false,
      },
      platformContext,
      ...(documentContext && { documentContext }),
    };
  };

  /** Disconnect any active SSE connection. */
  const disconnect = () => {
    if (abortControllerRef.current) {
      try {
        if (!abortControllerRef.current.signal.aborted) {
          abortControllerRef.current.abort();
        }
      } catch { /* ignore */ }
      abortControllerRef.current = null;
    }
  };

  /** Reset stream buffer state. */
  const resetStreamBuffer = () => {
    streamBufferRef.current = null;
  };

  /** Reset tool stream buffer state. */
  const resetToolStreamBuffer = () => {
    toolStreamBufferRef.current = null;
  };

  const sanitizeAssistantContent = (content: string): string => {
    if (!content) return content;
    let cleaned = content.replace(/<system-reminder>[\s\S]*?<\/system-reminder>/gi, '').trim();
    cleaned = cleaned.replace(/<system-reminder>[\s\S]*$/gi, '').trim();
    return cleaned;
  };

  /** POST to /api/v1/agents and process the SSE stream (with 401 retry). */
  const postAndStream = async (body: Record<string, unknown>, token: string, retryCount = 0): Promise<void> => {
    // Disconnect previous SSE connection before starting a new one
    disconnect();
    abortControllerRef.current = new AbortController();

    const response = await fetch(endpoints.agentExecute(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal: abortControllerRef.current.signal,
    });

    if (!response.ok) {
      // Retry on 401 with a refreshed token (matching hai_assistants pattern)
      if (response.status === 401 && retryCount < 2) {
        const newToken = await refreshToken();
        if (newToken) {
          return postAndStream(body, newToken, retryCount + 1);
        }
      }
      const errBody = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errBody || response.statusText}`);
    }

    await processSSEStream(response, token);
  };

  const sendMessage = useCallback(async (content: string, rawFiles?: File[], attachmentsMeta?: AttachmentMeta[]) => {
    if (!content.trim() && (!rawFiles || rawFiles.length === 0)) return;

    // Track chat message sent
    addDatadogAction('chat_message_sent', {
      session_id: sessionIdRef.current,
      agent_id: agentId,
      has_attachments: !!(rawFiles && rawFiles.length > 0),
      attachment_count: rawFiles?.length ?? 0,
    });

    setError(null);
    setChatState(ChatState.SENDING);
    resetStreamBuffer();
    resetToolStreamBuffer();

    // Build lightweight attachment indicators for the user message bubble
    const msgAttachments: ChatMessageAttachment[] | undefined =
      attachmentsMeta && attachmentsMeta.length > 0
        ? attachmentsMeta.map((a) => ({ fileId: a.fileId, fileName: a.fileName, fileSize: a.fileSize }))
        : undefined;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: Date.now(),
      ...(msgAttachments && { attachments: msgAttachments }),
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      const token = await getToken();
      const frontendContext = await buildFrontendContext();

      // Upload files to workspace session folder and build XML markup
      // (matches HAI assistants chat-message-processor flow)
      let promptWithAttachments = content;
      if (rawFiles && rawFiles.length > 0 && workspaceId) {
        const uploadSessionId = sessionIdRef.current || undefined; // undefined → 'tmp'
        const uploaded = await uploadAttachmentsForPrompt(workspaceId, rawFiles, uploadSessionId);
        if (uploaded.length > 0) {
          promptWithAttachments = content + buildAttachmentPromptSection(uploaded);
        }
      }

      if (sessionIdRef.current) {
        // Continue existing session — send as AGENT_ASK response
        await postAndStream({
          sessionId: sessionIdRef.current,
          eventType: 'AGENT_ASK',
          eventResponseData: { feedback: promptWithAttachments },
          frontendContext,
        }, token);
      } else {
        // Validate required fields for new chats (backend requires both)
        if (!agentId) {
          setError('Agent ID is required to start a new chat');
          setChatState(ChatState.IDLE);
          return;
        }
        if (!workspaceId) {
          setError('Workspace ID is required to start a new chat');
          setChatState(ChatState.IDLE);
          return;
        }

        // New chat — send humanPrompt with attachment markup
        await postAndStream({
          humanPrompt: promptWithAttachments,
          assistantId: agentId,
          workspaceId,
          frontendContext,
        }, token);
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Stream was likely replaced by a tool response stream.
        // Reset to IDLE after a short delay — if a new stream took over, it will
        // set its own state before this fires. If nothing took over, the UI unblocks.
        setTimeout(() => {
          setChatState(prev => prev !== ChatState.IDLE ? ChatState.IDLE : prev);
        }, 2000);
      } else {
        console.error('[useChat] sendMessage error:', err);
        addDatadogError(err instanceof Error ? err : new Error(String(err)), {
          source: 'chat_stream',
          session_id: sessionIdRef.current,
          agent_id: agentId,
        });
        setError(getErrorMessage(err));
        setChatState(ChatState.IDLE);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, workspaceId, officeHost, tools]);

  // ─── SSE Stream Processing ──────────────────

  /** Finalize any in-progress stream: flush buffered text into messages and go IDLE. */
  const finalizeStream = () => {
    if (streamBufferRef.current !== null && streamBufferRef.current.length > 0) {
      const finalContent = streamBufferRef.current;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && !last.toolCallId) {
          return [...prev.slice(0, -1), { ...last, content: finalContent }];
        }
        return prev;
      });
    }
    resetStreamBuffer();
    resetToolStreamBuffer();
    setChatState(ChatState.IDLE);
  };

  const processSSEStream = async (response: Response, token: string) => {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) throw new Error('No response body');

    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (!line || line.startsWith(':')) continue;

        if (line.startsWith('data: ')) {
          const data = line.slice(6);

          // Handle [DONE] signal — finalize and close
          if (data === '[DONE]') {
            finalizeStream();
            return;
          }

          try {
            const event: AgentSSEEvent = JSON.parse(data);
            await handleAgentEvent(event, token);
          } catch (err) {
            console.error('Failed to parse SSE event:', err, data);
          }
        }
      }
    }

    // Stream ended without [DONE] — still finalize
    finalizeStream();
  };

  const handleAgentEvent = async (event: AgentSSEEvent, token: string) => {
    const { eventType, eventData, eventActionRequired } = event;

    // Capture sessionId from any event that carries it (resilient to event ordering)
    const eventSessionId = (
      (event as unknown as Record<string, unknown>).sessionId ?? eventData.sessionId ?? eventData.session_id
    ) as string | undefined;
    if (eventSessionId && !sessionIdRef.current) {
      sessionIdRef.current = eventSessionId;
      setSessionId(eventSessionId);
      onSessionCreated?.(eventSessionId);
    }

    switch (eventType) {
      case 'AGENT_EXECUTION_STARTED': {
        setChatState(ChatState.REQUEST_IN_PROGRESS);
        break;
      }

      case 'SESSION_LOADED': {
        // Backend sends full event history when reconnecting to an existing session
        const sid = eventData.sessionId as string | undefined;
        if (sid) {
          sessionIdRef.current = sid;
          setSessionId(sid);
          onSessionCreated?.(sid);
        }

        // Backend sends eventData.events (array) and eventData.sessionData (metadata)
        const events = (eventData.events ?? eventData.eventHistory) as EventHistoryEntry[] | undefined;
        const sessionData = eventData.sessionData as Record<string, unknown> | undefined;

        if (sessionData) {
          setIsArchived(sessionData.isArchivedAssistant === true);
        }

        if (events && events.length > 0) {
          const loadedMessages = processEventHistoryToMessages(events);
          if (loadedMessages.length > 0) {
            setMessages(loadedMessages);
          }
        }
        setChatState(ChatState.IDLE);
        break;
      }

      case 'AGENT_RESPONSE_CHUNK': {
        const chunk = (eventData.content ?? eventData.chunk ?? eventData.text ?? eventData.aiMessage ?? '') as string;
        const toolDetail = eventData.toolDetail as Record<string, unknown> | undefined;
        const isToolGenerating = eventData.isToolGenerating as boolean | undefined;

        // === Handle Text Streaming ===
        if (chunk && !isToolGenerating) {
          setChatState(ChatState.STREAMING);

          // Accumulate in buffer
          if (streamBufferRef.current === null) {
            streamBufferRef.current = '';
          }
          streamBufferRef.current += chunk;

          // Snapshot the buffer NOW — don't read the ref inside the updater
          // (React may batch and run the updater after the ref is reset by [DONE])
          const snapshotContent = sanitizeAssistantContent(streamBufferRef.current);
          if (snapshotContent.trim()) {
            lastAssistantContentRef.current = snapshotContent;
          }

          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.toolCallId) {
              return [
                ...prev.slice(0, -1),
                { ...last, content: snapshotContent },
              ];
            }
            return [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: 'assistant' as const,
              content: snapshotContent,
                timestamp: Date.now(),
              },
            ];
          });
        }

        // === Handle Tool Call Streaming ===
        if (toolDetail && isToolGenerating) {
          const toolType = toolDetail.type as string | undefined;
          const toolName = toolDetail.name as string | undefined;
          const toolId = toolDetail.id as string | undefined;
          const toolArgs = toolDetail.args as string | undefined;
          const toolIndex = toolDetail.index as number | undefined;

          // Start new tool call buffer when we see a tool name and ID
          if (toolName && toolId && toolType === 'tool_call_chunk') {
            if (!toolStreamBufferRef.current || toolStreamBufferRef.current.id !== toolId) {
              // Initialize new tool buffer
              toolStreamBufferRef.current = {
                id: toolId,
                name: toolName,
                args: '',
                index: toolIndex ?? 0,
              };

              // Flush any pending text before showing tool card
              if (streamBufferRef.current && streamBufferRef.current.length > 0) {
                const bufferedText = streamBufferRef.current;
                setMessages(prev => {
                  const last = prev[prev.length - 1];
              if (last?.role === 'assistant' && !last.toolCallId) {
                const sanitized = sanitizeAssistantContent(bufferedText);
                if (sanitized.trim()) {
                  lastAssistantContentRef.current = sanitized;
                }
                return [...prev.slice(0, -1), { ...last, content: sanitized }];
              }
              return prev;
            });
                resetStreamBuffer();
              }

            }
          }

          // Accumulate args chunks
          if (toolArgs !== undefined && toolStreamBufferRef.current) {
            toolStreamBufferRef.current.args += toolArgs;

            // Try to parse accumulated args and update message
            const currentBuffer = toolStreamBufferRef.current;
            let parsedArgs: Record<string, unknown> | null = null;
            let isComplete = false;

            try {
              // Attempt to parse JSON (may be incomplete during streaming)
              if (currentBuffer.args.trim()) {
                parsedArgs = JSON.parse(currentBuffer.args);
                isComplete = true;
              }
            } catch {
              // Incomplete JSON, wait for more chunks
              // Show partial raw string in the meantime
              parsedArgs = { _partial: currentBuffer.args };
            }

            // Update existing tool message (or create once args are complete)
            setMessages(prev => {
              const matchIdx = prev.findIndex(m => m.toolCallId === currentBuffer.id);
              if (matchIdx >= 0) {
                const updated = [...prev];
                updated[matchIdx] = {
                  ...updated[matchIdx],
                  toolArgs: parsedArgs ?? updated[matchIdx].toolArgs,
                };
                return updated;
              }
              if (isComplete && currentBuffer.name) {
                return [...prev, {
                  id: crypto.randomUUID(),
                  role: 'assistant',
                  content: getToolDisplayName(currentBuffer.name, 'pending'),
                  timestamp: Date.now(),
                  toolCallId: currentBuffer.id,
                  toolName: currentBuffer.name,
                  toolArgs: parsedArgs ?? {},
                  toolStatus: 'pending',
                }];
              }
              return prev;
            });
          }
        }

        break;
      }

      case 'AGENT_RESPONSE':
      case 'RESPONSE': {
        const rawContent = (eventData.content ?? eventData.text ?? eventData.message ?? eventData.aiMessage ?? '') as string;
        const content = sanitizeAssistantContent(rawContent);

        if (streamBufferRef.current !== null) {
          // Stream was active — mark last assistant message as complete
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.toolCallId) {
              return [...prev.slice(0, -1), { ...last }];
            }
            return prev;
          });
        } else if (content) {
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (lastAssistantContentRef.current === content) {
              return prev;
            }
            if (last?.role === 'assistant' && !last.toolCallId && last.content === content) {
              return prev;
            }
            if (last?.role === 'assistant' && !last.toolCallId) {
              return [...prev.slice(0, -1), { ...last, content }];
            }
            return [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: 'assistant' as const,
                content,
                timestamp: Date.now(),
              },
            ];
          });
          if (content.trim()) {
            lastAssistantContentRef.current = content;
          }
        }
        resetStreamBuffer();
        break;
      }

      case 'EXTERNAL_TOOL_INVOKE': {
        // Backend dispatches an external (Office.js) tool for frontend execution.
        const toolDetail = eventData.toolDetail as Record<string, unknown> | undefined;
        const toolName = (toolDetail?.name ?? eventData.toolName ?? eventData.name ?? '') as string;
        const toolArgs = (toolDetail?.args ?? eventData.toolArgs ?? eventData.args ?? eventData.input ?? {}) as Record<string, unknown>;
        const toolCallId = (toolDetail?.id ?? eventData.toolCallId ?? event.eventId ?? crypto.randomUUID()) as string;

        // Debug: uncomment to trace EXTERNAL_TOOL_INVOKE arg extraction
        const aiMessageRaw = (eventData.aiMessage ?? '') as string;
        const aiMessage = sanitizeAssistantContent(aiMessageRaw);

        // Flush any streamed agent text that arrived before this tool call
        if (streamBufferRef.current !== null && streamBufferRef.current.length > 0) {
          const bufferedText = streamBufferRef.current;
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.toolCallId) {
              const sanitized = sanitizeAssistantContent(bufferedText);
              if (sanitized.trim()) {
                lastAssistantContentRef.current = sanitized;
              }
              return [...prev.slice(0, -1), { ...last, content: sanitized }];
            }
            return prev;
          });
          resetStreamBuffer();
        } else if (aiMessage) {
          // No streamed chunks, but backend included the agent's reasoning text
          // Only create new message if the last message doesn't already have this content
          setMessages(prev => {
            const last = prev[prev.length - 1];
            // Skip if last message is assistant with same content (avoid duplicates)
            if (last?.role === 'assistant' && !last.toolCallId && last.content === aiMessage) {
              return prev;
            }
            if (lastAssistantContentRef.current === aiMessage) {
              return prev;
            }
            return [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: 'assistant' as const,
                content: aiMessage,
                timestamp: Date.now(),
              },
            ];
          });
          if (aiMessage.trim()) {
            lastAssistantContentRef.current = aiMessage;
          }
        }
        resetStreamBuffer();

        // Add/update tool message for frontend tools only
        const resolved = resolveTool(toolName);
        if (resolved?.handler) {
          setMessages(prev => {
            const matchIdx = findMatchingToolIndex(prev, toolCallId, toolName);
            if (matchIdx >= 0) {
              const updated = [...prev];
              const existing = updated[matchIdx];
              updated[matchIdx] = {
                ...existing,
                toolCallId: existing.toolCallId ?? toolCallId,
                toolArgs: Object.keys(toolArgs).length > 0 ? toolArgs : existing.toolArgs,
                toolStatus: 'pending' as const,
              };
              return updated;
            }
            return [...prev, {
              id: crypto.randomUUID(),
              role: 'assistant' as const,
              content: getToolDisplayName(toolName || 'tool', 'pending'),
              timestamp: Date.now(),
              toolCallId,
              toolName: toolName || 'external_tool',
              toolArgs,
              toolStatus: 'pending' as const,
            }];
          });

          // Keep chat state active so stop button remains visible
          setChatState(ChatState.REQUEST_IN_PROGRESS);
          externalToolRunningRef.current = true;
          externalToolAbortRef.current = new AbortController();

          // Fire tool execution without blocking the SSE stream
          executeExternalTool(toolCallId, toolName, toolArgs, token).catch(err => {
            console.error(`[useChat] External tool ${toolName} failed:`, err);
          }).finally(() => {
            externalToolRunningRef.current = false;
            externalToolAbortRef.current = null;
          });
        }
        break;
      }

      case 'TOOL_USE': {
        // Backend tool usage — may require approval
        const toolDetail = eventData.toolDetail as Record<string, unknown> | undefined;
        const toolName = (toolDetail?.name ?? eventData.toolName ?? '') as string;
        const toolArgs = (toolDetail?.args ?? eventData.toolArgs ?? {}) as Record<string, unknown>;
        const toolCallId = (toolDetail?.id ?? eventData.toolCallId ?? event.eventId ?? crypto.randomUUID()) as string;

        lastToolRejectedRef.current = false;

        const resolvedTool = resolveTool(toolName);
        const isOfficeTool = !!resolvedTool?.handler;
        // Auto-approve code execution in the Office Add-in (no user gate needed)
        const isCodeExecution = toolName === 'execute_command';
        const requiresApproval = !!eventActionRequired && !isOfficeTool && !isCodeExecution;

        setMessages(prev => {
          const matchIdx = findMatchingToolIndex(prev, toolCallId, toolName);
          if (matchIdx >= 0) {
            const updated = [...prev];
            const existing = updated[matchIdx];
            updated[matchIdx] = {
              ...existing,
              toolCallId: existing.toolCallId ?? toolCallId,
              toolArgs: Object.keys(toolArgs).length > 0 ? toolArgs : existing.toolArgs,
              toolStatus: requiresApproval ? 'awaiting_approval' : 'pending',
              content: getToolDisplayName(toolName, 'pending'),
            };
            return updated;
          }
          return [...prev, {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: getToolDisplayName(toolName, 'pending'),
            timestamp: Date.now(),
            toolCallId,
            toolName,
            toolArgs,
            toolStatus: requiresApproval ? 'awaiting_approval' : 'pending',
          }];
        });

        if (isOfficeTool) {
          // Office tool — always auto-approve at TOOL_USE level.
          await sendEventResponse('TOOL_USE', { selectedValue: 'AllowOnce' }, token);
          return;
        }

        if (requiresApproval) {
          pendingApprovalRef.current.set(toolCallId, { toolCallId, toolName, toolArgs, token });
          break;
        }

        // Auto-approve backend tools when no approval is required
        await sendEventResponse('TOOL_USE', { selectedValue: 'AllowOnce' }, token);
        break;
      }

      case 'TOOL_RESPONSE': {
        setChatState(ChatState.REQUEST_IN_PROGRESS);
        // Tool finished executing — update existing message or create new one
        const trToolDetail = eventData.toolDetail as Record<string, unknown> | undefined;
        const trToolName = (trToolDetail?.name ?? eventData.toolName ?? '') as string;
        const trToolArgs = (trToolDetail?.args ?? eventData.toolArgs ?? {}) as Record<string, unknown>;
        const trToolCallId = (trToolDetail?.id ?? eventData.toolCallId ?? event.eventId ?? '') as string;
        const result = eventData.result ?? eventData.output ?? eventData.aiMessage;

        // Flush any buffered agent text before showing tool result
        if (streamBufferRef.current !== null && streamBufferRef.current.length > 0) {
          const bufferedText = streamBufferRef.current;
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.toolCallId) {
              return [...prev.slice(0, -1), { ...last, content: bufferedText }];
            }
            return prev;
          });
          resetStreamBuffer();
        }

        setMessages(prev => {
          const matchIdx = findMatchingToolIndex(prev, trToolCallId, trToolName);
          if (matchIdx >= 0) {
            const updated = [...prev];
            const existingToolName = updated[matchIdx].toolName || trToolName;
            updated[matchIdx] = {
              ...updated[matchIdx],
              content: getToolDisplayName(existingToolName, 'completed'),
              toolStatus: 'success' as const,
              toolResult: result,
              toolArgs: Object.keys(updated[matchIdx].toolArgs || {}).length > 0
                ? updated[matchIdx].toolArgs
                : (Object.keys(trToolArgs).length > 0 ? trToolArgs : updated[matchIdx].toolArgs),
            };
            return updated;
          }

          if (lastToolRejectedRef.current) {
            return prev;
          }

          // Fallback: update the most recent pending/awaiting tool message
          const lastPendingIdx = (() => {
            for (let i = prev.length - 1; i >= 0; i--) {
              const msg = prev[i];
              if (msg.toolCallId && (msg.toolStatus === 'pending' || msg.toolStatus === 'awaiting_approval')) {
                return i;
              }
            }
            return -1;
          })();
          if (lastPendingIdx >= 0) {
            const updated = [...prev];
            const existingToolName = updated[lastPendingIdx].toolName || trToolName;
            updated[lastPendingIdx] = {
              ...updated[lastPendingIdx],
              content: getToolDisplayName(existingToolName, 'completed'),
              toolStatus: 'success' as const,
              toolResult: result,
              toolArgs: Object.keys(updated[lastPendingIdx].toolArgs || {}).length > 0
                ? updated[lastPendingIdx].toolArgs
                : (Object.keys(trToolArgs).length > 0 ? trToolArgs : updated[lastPendingIdx].toolArgs),
            };
            return updated;
          }

          // No matching message — create a full tool card (auto-approved tools skip TOOL_USE)
          if (trToolName) {
            return [...prev, {
              id: crypto.randomUUID(),
              role: 'assistant' as const,
              content: getToolDisplayName(trToolName, 'completed'),
              timestamp: Date.now(),
              toolCallId: trToolCallId || crypto.randomUUID(),
              toolName: trToolName,
              toolArgs: Object.keys(trToolArgs).length > 0 ? trToolArgs : undefined,
              toolStatus: 'success' as const,
              toolResult: result,
            }];
          }
          return prev;
        });
        resetStreamBuffer();
        break;
      }

      case 'TOOL_POST_APPROVAL': {
        // Auto-approve file modifications
        if (eventActionRequired) {
          await sendEventResponse('TOOL_POST_APPROVAL', { selectedValue: 'ApproveChanges' }, token);
        }
        break;
      }

      case 'TOOL_USE_ERROR': {
        const tueToolDetail = eventData.toolDetail as Record<string, unknown> | undefined;
        const tueToolCallId = (tueToolDetail?.id ?? eventData.toolCallId ?? event.eventId ?? '') as string;
        const errMsg = (eventData.aiMessage ?? eventData.error ?? eventData.message ?? 'Tool error') as string;

        setMessages(prev => {
          const matchIdx = findMatchingToolIndex(prev, tueToolCallId, tueToolDetail?.name as string | undefined);
          if (matchIdx >= 0) {
            const updated = [...prev];
            updated[matchIdx] = {
              ...updated[matchIdx],
              content: `${updated[matchIdx].toolName} failed`,
              toolStatus: 'error' as const,
              toolError: errMsg,
            };
            return updated;
          }
          return prev;
        });
        break;
      }

      case 'AGENT_ASK':
      case 'SYSTEM_ASK': {
        // Agent is asking a follow-up question — the next user message will respond.
        // The backend sends: aiMessage (the AI's question) and humanMessage (often '/exit').
        // We display aiMessage/content, NOT humanMessage.
        const question = (eventData.aiMessage ?? eventData.content ?? eventData.question ?? eventData.message ?? '') as string;
        if (question && question !== '/exit') {
          setMessages(prev => [
            ...prev,
            {
              id: crypto.randomUUID(),
              role: 'assistant' as const,
              content: question,
              timestamp: Date.now(),
            },
          ]);
        }
        resetStreamBuffer();
        setChatState(ChatState.IDLE);
        break;
      }

      case 'CONNECTORS_STATUS_UPDATE': {
        const statuses = (eventData.connectors ?? eventData.statuses ?? []) as Array<{
          id: number; name: string; status: string;
          oauth?: { status: string; authorizationUrl?: string };
        }>;
        if (statuses.length > 0) {
          setConnectorStatuses(statuses);
        }

        // Extract active skills from the update if present
        const skills = eventData.skills as Array<{ name: string; description: string }> | undefined;
        if (skills && skills.length > 0) {
          setActiveSkills(skills);
        }
        break;
      }

      case 'UPDATE_SESSION': {
        const sid = eventData.sessionId as string | undefined;
        if (sid && !sessionIdRef.current) {
          sessionIdRef.current = sid;
          setSessionId(sid);
          onSessionCreated?.(sid);
        }
        break;
      }

      case 'ERROR':
      case 'AGENT_ERROR': {
        // Flush any partial stream buffer before showing error
        if (streamBufferRef.current) {
          const buffered = streamBufferRef.current;
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.toolCallId) {
              return [...prev.slice(0, -1), { ...last, content: buffered }];
            }
            return prev;
          });
        }
        resetStreamBuffer();

        const info = eventData.info as Record<string, unknown> | undefined;
        const message = (info?.message ?? eventData.aiMessage ?? eventData.message ?? eventData.error ?? 'An error occurred') as unknown;
        setError(getErrorMessage(message));
        setChatState(ChatState.IDLE);
        break;
      }

      case 'USER_STOP': {
        // Flush any partial stream buffer
        if (streamBufferRef.current) {
          const buffered = streamBufferRef.current;
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.toolCallId) {
              return [...prev.slice(0, -1), { ...last, content: buffered }];
            }
            return prev;
          });
        }
        resetStreamBuffer();
        setChatState(ChatState.IDLE);
        break;
      }

      case 'SSE_DISCONNECT': {
        // Don't reset to IDLE if an external tool is still executing —
        // the SSE disconnects while the backend waits for tool results.
        if (!externalToolRunningRef.current) {
          setChatState(ChatState.IDLE);
        }
        break;
      }

      case 'AGENT_INTERRUPT': {
        resetStreamBuffer();
        setChatState(ChatState.IDLE);
        break;
      }

      default:
        // Unknown event type — ignore
        break;
    }
  };

  // ─── External Tool Execution (Office.js tools) ───────────

  const normalizeToolArgs = (toolName: string, args: Record<string, unknown>) => {
    const normalized: Record<string, unknown> = { ...args };

    const setIfDefined = (key: string, value: unknown) => {
      if (value !== undefined) normalized[key] = value;
    };

    const slideIndex = args.slide_index ?? args.slideIndex;
    if (slideIndex !== undefined) setIfDefined('slide_index', Number(slideIndex));

    switch (toolName) {
      case 'edit_slide_content': {
        if (args.slideIndex !== undefined) setIfDefined('slide_index', Number(args.slideIndex));
        if (args.slideData !== undefined) setIfDefined('slide', args.slideData);
        if (args.slide_data !== undefined) setIfDefined('slide', args.slide_data);
        if (args.elements !== undefined && args.slide === undefined) {
          setIfDefined('slide', {
            background: args.background,
            elements: args.elements,
          });
        }
        if (typeof normalized.slide === 'string') {
          try {
            normalized.slide = JSON.parse(normalized.slide as string);
          } catch {
            // leave as-is; handler will surface error
          }
        }
        if (typeof normalized.slide === 'object' && normalized.slide !== null) {
          const slide = normalized.slide as Record<string, unknown>;
          if (typeof slide.elements === 'string') {
            try {
              slide.elements = JSON.parse(slide.elements);
            } catch {
              // leave as-is; handler will surface error
            }
          }
        }
        break;
      }
      case 'get_slide_shapes':
      case 'get_shape_text':
      case 'update_shape_text':
      case 'update_shape':
      case 'delete_shape': {
        setIfDefined('shape_id', args.shape_id ?? args.shapeId);
        break;
      }
      case 'set_table_cell_text': {
        setIfDefined('shape_id', args.shape_id ?? args.shapeId);
        if (args.row !== undefined) setIfDefined('row', Number(args.row));
        if (args.column !== undefined) setIfDefined('column', Number(args.column));
        break;
      }
      case 'add_shape': {
        setIfDefined('shape_type', args.shape_type ?? args.shapeType);
        break;
      }
      case 'add_text_box': {
        if (args.text !== undefined) setIfDefined('text', String(args.text));
        break;
      }
      case 'add_image': {
        setIfDefined('base64_image', args.base64_image ?? args.base64Image);
        break;
      }
      case 'move_slide': {
        setIfDefined('new_index', args.new_index ?? args.newIndex);
        break;
      }
      case 'set_slide_background': {
        if (args.color !== undefined) setIfDefined('color', String(args.color));
        break;
      }
      default:
        break;
    }

    return normalized;
  };

  const executeExternalTool = async (
    toolCallId: string,
    toolName: string,
    args: Record<string, unknown>,
    token: string,
  ) => {
    // Capture sessionId upfront — cancelRequest may clear sessionIdRef during execution
    const capturedSessionId = sessionIdRef.current ?? sessionId;
    const resolved = resolveTool(toolName);
    const handler = resolved?.handler;

    let responseData: Record<string, unknown>;

    if (!handler) {
      console.error('[useChat] Tool handler not found:', toolName);
      setMessages(prev => prev.map(msg =>
        msg.toolCallId === toolCallId
          ? { ...msg, content: `${toolName} — tool not found`, toolStatus: 'error' as const, toolError: `Tool ${toolName} not found` }
          : msg
      ));
      responseData = { success: false, error: `Tool ${toolName} not found` };
    } else {
      try {
        let normalizedArgs = normalizeToolArgs(toolName, args);

        // Resolve skill asset references for generate_template_slide
        if (toolName === 'generate_template_slide' && workspaceId) {
          normalizedArgs = await resolveTemplateSlideAssets(normalizedArgs, workspaceId);
        }

        const toolStartTime = performance.now();
        const result = await handler(normalizedArgs);
        const toolDurationMs = Math.round(performance.now() - toolStartTime);
        const cancelled = !!externalToolAbortRef.current?.signal.aborted;

        // Track tool execution in Datadog — includes result summary
        const isCodeExec = toolName === 'execute_command';
        const isBatch = toolName === 'excel_batch_operations' || toolName === 'word_batch_operations';
        const resultSummary: Record<string, unknown> = {};
        let hasResultError = false;
        if (result && typeof result === 'object') {
          const r = result as Record<string, unknown>;
          if ('success' in r) {
            resultSummary.result_success = r.success;
            if (r.success === false) hasResultError = true;
          }
          if ('totalOperations' in r) resultSummary.total_operations = r.totalOperations;
          if ('succeeded' in r) resultSummary.operations_succeeded = r.succeeded;
          if ('failed' in r) {
            resultSummary.operations_failed = r.failed;
            if (typeof r.failed === 'number' && r.failed > 0) hasResultError = true;
          }
          if ('range' in r) resultSummary.result_range = r.range;
          if ('chartType' in r) resultSummary.result_chart_type = r.chartType;
          if ('replacedCount' in r) resultSummary.result_replaced_count = r.replacedCount;
          // Detect error content in the result string (e.g., PPT render errors)
          if ('message' in r && typeof r.message === 'string' && /fail|error/i.test(r.message)) {
            resultSummary.result_error_message = (r.message as string).substring(0, 500);
            hasResultError = true;
          }
          if ('error' in r && r.error) {
            resultSummary.result_error = typeof r.error === 'string' ? r.error.substring(0, 500) : String(r.error);
            hasResultError = true;
          }
        }
        // Also check if the stringified result contains error text (PPT generator returns errors in result body)
        if (!hasResultError && result && typeof result === 'object') {
          const resultStr = JSON.stringify(result);
          if (/Failed to render|TOOL EXECUTION FAILED/i.test(resultStr)) {
            hasResultError = true;
            resultSummary.has_partial_error = true;
          }
        }

        const toolSuccess = !cancelled && !hasResultError;
        addDatadogAction(isCodeExec ? 'code_execution' : 'tool_execution', {
          tool_name: toolName,
          tool_call_id: toolCallId,
          duration_ms: toolDurationMs,
          success: toolSuccess,
          has_partial_error: hasResultError && !cancelled,
          cancelled,
          is_batch: isBatch,
          session_id: sessionIdRef.current,
          ...resultSummary,
        });

        // Report partial errors to Datadog error tracking too
        if (hasResultError && !cancelled) {
          addDatadogError(new Error(`Tool ${toolName} completed with errors`), {
            source: 'tool_partial_error',
            tool_name: toolName,
            tool_call_id: toolCallId,
            session_id: sessionIdRef.current,
            ...resultSummary,
          });
        }

        // Check if cancelled while tool was running
        if (cancelled) {
          responseData = { success: false, error: 'Tool execution cancelled by user' };
          setMessages(prev => prev.map(msg =>
            msg.toolCallId === toolCallId
              ? { ...msg, content: `${toolName} cancelled`, toolStatus: 'error' as const, toolError: 'Cancelled' }
              : msg
          ));
        } else {
          // If the tool returned a _screenshot, upload the image to workspace
          // storage (same flow as chat file attachments) so the backend's
          // read_image tool can load it for vision-capable LLMs.
          if (result && typeof result === 'object' && '_screenshot' in (result as Record<string, unknown>)) {
            const raw = result as Record<string, unknown>;
            const screenshot = raw._screenshot as { base64Data: string; mimeType: string };
            const slideIdx = (raw.slideIndex as number) ?? 0;
            const filename = `slide_${slideIdx + 1}_screenshot.png`;

            // Convert base64 → File, upload to session folder, and build
            // the storage path in the same format as chat file attachments
            // (shared_workspace/HAI-ASSISTANTS-WORKSPACES/…) so that
            // read_image can resolve it within its allowed directories.
            let filePath = filename;
            try {
              const byteString = atob(screenshot.base64Data);
              const bytes = new Uint8Array(byteString.length);
              for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
              const blob = new Blob([bytes], { type: screenshot.mimeType });
              const file = new File([blob], filename, { type: screenshot.mimeType });

              if (workspaceId) {
                // uploadAttachmentsForPrompt uploads + builds the correct
                // shared_workspace/… storage path (same as regular attachments).
                const uploaded = await uploadAttachmentsForPrompt(
                  workspaceId,
                  [file],
                  sessionIdRef.current || undefined,
                );
                if (uploaded.length > 0) {
                  filePath = uploaded[0].path;
                }
              }
            } catch (uploadErr) {
              console.warn('[useChat] Screenshot upload failed, using filename only:', uploadErr);
            }

            const toolResult = {
              description: raw.description,
              slideIndex: slideIdx,
              filePath,
            };

            setMessages(prev => prev.map(msg =>
              msg.toolCallId === toolCallId
                ? { ...msg, content: getToolDisplayName(toolName, 'completed'), toolStatus: 'success' as const, toolResult }
                : msg
            ));
            responseData = { success: true, result: toolResult };
          } else {
            setMessages(prev => prev.map(msg =>
              msg.toolCallId === toolCallId
                ? { ...msg, content: getToolDisplayName(toolName, 'completed'), toolStatus: 'success' as const, toolResult: result }
                : msg
            ));
            responseData = { success: true, result };
          }
        }
      } catch (err: unknown) {
        const errMsg = externalToolAbortRef.current?.signal.aborted
          ? 'Tool execution cancelled by user'
          : getErrorMessage(err);

        // Track tool error in Datadog
        addDatadogError(err instanceof Error ? err : new Error(errMsg), {
          source: 'tool_execution',
          tool_name: toolName,
          tool_call_id: toolCallId,
          session_id: sessionIdRef.current,
        });

        setMessages(prev => prev.map(msg =>
          msg.toolCallId === toolCallId
            ? { ...msg, content: `${toolName} failed: ${errMsg}`, toolStatus: 'error' as const, toolError: errMsg }
            : msg
        ));
        
        // IMPORTANT: Backend extracts only the 'result' field, so we need to put the error there
        // Format: Backend will stringify this and pass to LLM
        responseData = { 
          success: false, 
          error: errMsg,
          result: {
            success: false,
            error: errMsg,
            _error: `TOOL EXECUTION FAILED: ${errMsg}`,
          }
        };
      }
    }

    // Don't send result if cancelled
    if (externalToolAbortRef.current?.signal.aborted) {
      return;
    }

    // Send result back to backend and process the follow-up SSE stream.
    // This opens a new SSE connection for the agent's response after receiving the tool result.
    // Use capturedSessionId (saved at start) since cancelRequest may have cleared sessionIdRef.
    if (capturedSessionId) {
      const frontendContext = await buildFrontendContext();
      try {
        await postAndStream({
          sessionId: capturedSessionId,
          eventType: 'EXTERNAL_TOOL_INVOKE',
          eventResponseData: responseData,
          frontendContext,
        }, token);
      } catch (streamErr) {
        // If SSE stream fails, fall back to plain POST so backend isn't stuck
        // waiting for the tool result (prevents "active agent execution" errors).
        console.warn('[useChat] postAndStream failed after tool execution, falling back to plain POST:', streamErr);
        try {
          await apiFetch(endpoints.chatToolResult(), {
            method: 'POST',
            body: JSON.stringify({
              sessionId: capturedSessionId,
              eventType: 'EXTERNAL_TOOL_INVOKE',
              eventResponseData: responseData,
            }),
          });
        } catch (fallbackErr) {
          console.error('[useChat] Fallback tool result POST also failed:', fallbackErr);
        }
        setChatState(ChatState.IDLE);
      }
    } else {
      console.error('[useChat] Tool result sent without sessionId');
      setError('No active session. Cannot send tool response.');
      setChatState(ChatState.IDLE);
    }
  };

  const findMatchingToolIndex = (
    messages: ChatMessage[],
    toolCallId?: string,
    toolName?: string,
  ): number => {
    if (toolCallId) {
      const idx = messages.findIndex(m => m.toolCallId === toolCallId);
      if (idx >= 0) return idx;
    }
    if (toolName) {
      const candidates = messages
        .map((m, index) => ({ m, index }))
        .filter(({ m }) => m.toolName === toolName)
        .filter(({ m }) => !m.toolStatus || m.toolStatus === 'pending' || m.toolStatus === 'awaiting_approval');
      if (candidates.length === 1) return candidates[0].index;
    }
    return -1;
  };

  // ─── Tool Approval Handlers ─────────────────

  const approveToolCall = useCallback(async (toolCallId: string) => {
    const pending = pendingApprovalRef.current.get(toolCallId);
    if (!pending) return;
    pendingApprovalRef.current.delete(toolCallId);
    lastToolRejectedRef.current = false;

    // Update message status to pending (executing)
    setMessages(prev => prev.map(msg =>
      msg.toolCallId === toolCallId
        ? { ...msg, content: `Calling ${msg.toolName}...`, toolStatus: 'pending' as const }
        : msg
    ));
    setChatState(ChatState.REQUEST_IN_PROGRESS);

    try {
      // Approval happened at TOOL_USE stage. Send ALLOW_ONCE to resume the backend.
      // The backend will then proceed to EXTERNAL_TOOL_INVOKE, which the frontend
      // will auto-execute (no second approval needed).
      await sendEventResponse('TOOL_USE', { selectedValue: 'AllowOnce' }, pending.token);
    } catch (err: unknown) {
      const errMsg = getErrorMessage(err);
      console.error('[useChat] approveToolCall error:', errMsg);

      // Update the tool message to show the error
      setMessages(prev => prev.map(msg =>
        msg.toolCallId === toolCallId
          ? { ...msg, content: `${msg.toolName} failed: ${errMsg}`, toolStatus: 'error' as const, toolError: errMsg }
          : msg
      ));
      setError(`Tool approval failed: ${errMsg}`);
      setChatState(ChatState.IDLE);
    }
  }, []);

  const rejectToolCall = useCallback(async (toolCallId: string, feedback?: string) => {
    const pending = pendingApprovalRef.current.get(toolCallId);
    if (!pending) return;
    pendingApprovalRef.current.delete(toolCallId);
    lastToolRejectedRef.current = true;

    // Update message status to error (rejected)
    setMessages(prev => prev.map(msg =>
      msg.toolCallId === toolCallId
        ? { ...msg, content: `Rejected ${msg.toolName}`, toolStatus: 'error' as const, toolError: feedback || 'Rejected by user' }
        : msg
    ));

    try {
      // Send rejection as a TOOL_USE rejection (approval happens at TOOL_USE stage)
      await sendEventResponse('TOOL_USE', {
        selectedValue: 'RejectWithFeedback',
        feedback: feedback || 'TOOL FAILED: Tool execution rejected by user. User is not satisfied with the tool execution. STOP using the tools anymore. Ask the user why it was rejected and what to do next.',
      }, pending.token);
    } catch (err: unknown) {
      const errMsg = getErrorMessage(err);
      console.error('[useChat] rejectToolCall error:', errMsg);
      setError(`Tool rejection failed: ${errMsg}`);
      setChatState(ChatState.IDLE);
    }
  }, []);

  /** Send an event response back to the agents-backend (resumes the SSE stream). */
  const sendEventResponse = async (
    eventType: string,
    eventResponseData: Record<string, unknown>,
    token: string,
  ) => {
    const currentSessionId = sessionIdRef.current ?? sessionId;
    if (!currentSessionId) {
      console.error('[useChat] sendEventResponse called without a session ID');
      setError('No active session. Cannot send tool response.');
      setChatState(ChatState.IDLE);
      return;
    }

    const frontendContext = await buildFrontendContext();

    await postAndStream({
      sessionId: currentSessionId,
      eventType,
      eventResponseData,
      frontendContext,
    }, token);
  };

  // ─── Session loading & reconnection ────────────────────────

  const loadSession = useCallback(async (sessionIdToLoad: string) => {
    setError(null);
    setChatState(ChatState.RECONNECTING);

    // Disconnect any active stream before switching sessions
    disconnect();
    resetStreamBuffer();
    setMessages([]);

    // Set session ID immediately so UI knows we're loading this session
    sessionIdRef.current = sessionIdToLoad;
    setSessionId(sessionIdToLoad);
    onSessionCreated?.(sessionIdToLoad);

    try {
      const token = await getToken();

      // The stream endpoint (/sessions/{id}/stream) delivers a SESSION_LOADED
      // event with the full event history. processEventHistoryToMessages (called
      // from the SESSION_LOADED handler) reconstructs chat messages properly,
      // including tool calls with their results.
      // GET /sessions/{id} only returns a SessionSummary with no messages.
      await reconnectToStream(sessionIdToLoad, token);
    } catch (err: unknown) {
      console.error('Failed to load session:', err);
      setError(getErrorMessage(err, 'Failed to load session'));
      setChatState(ChatState.IDLE);
    }
  }, [onSessionCreated]);

  /** Reconnect to a session's SSE stream to receive any pending events (e.g. in-progress tools). */
  const reconnectToStream = async (sid: string, token: string) => {
    try {
      disconnect();
      abortControllerRef.current = new AbortController();

      const url = `${endpoints.sessions.stream(sid)}?limit=100&beforeMessageOrder=-1`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok || !response.body) {
        // Not an error — session may just have no active stream
        setChatState(ChatState.IDLE);
        return;
      }

      await processSSEStream(response, token);
    } catch (err: unknown) {
      // Ignore abort errors (expected when user navigates away)
      if (err instanceof Error && err.name === 'AbortError') return;
      // Stream reconnect completed or unavailable
    } finally {
      // Ensure we don't stay stuck in RECONNECTING
      setChatState((prev) => prev === ChatState.RECONNECTING ? ChatState.IDLE : prev);
    }
  };

  // ─── Question handlers ───────────────────────

  const handleQuestionComplete = useCallback((answers: Record<string, string>) => {
    pendingQuestion?.resolve(answers);
  }, [pendingQuestion]);

  const handleQuestionSkip = useCallback(() => {
    pendingQuestion?.resolve({ _skipped: 'true' });
  }, [pendingQuestion]);

  const handleQuestionDismiss = useCallback(() => {
    pendingQuestion?.resolve({ _skipped: 'true' });
  }, [pendingQuestion]);

  // ─── Lifecycle ────────────────────────────────

  const cancelRequest = useCallback(async () => {
    // Capture sessionId upfront before any state clearing
    const sid = sessionIdRef.current;
    setChatState(ChatState.STOPPING);
    disconnect();
    // Abort any running external tool (Office.js)
    if (externalToolAbortRef.current) {
      externalToolAbortRef.current.abort();
      externalToolAbortRef.current = null;
    }
    externalToolRunningRef.current = false;
    // Flush stream buffer
    if (streamBufferRef.current) {
      const buffered = streamBufferRef.current;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && !last.toolCallId) {
          return [...prev.slice(0, -1), { ...last, content: buffered }];
        }
        return prev;
      });
    }
    resetStreamBuffer();
    resetToolStreamBuffer();

    // Notify backend to stop current session generation (if any).
    // Skip if no session was ever created (user never sent a message).
    if (sid) {
      try {
        const token = await getToken();
        await fetch(endpoints.sessions.stop(sid), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // Ignore stop errors to avoid blocking UI
      }
    }
    setChatState(ChatState.IDLE);
  }, [getToken]);

  const clearMessages = useCallback(() => {
    disconnect();
    resetStreamBuffer();
    resetToolStreamBuffer();
    setMessages([]);
    setSessionId(undefined);
    sessionIdRef.current = undefined;
    setError(null);
    setChatState(ChatState.IDLE);
    setIsArchived(false);
    setActiveSkills([]);
    setConnectorStatuses([]);
  }, []);

  // Load session on mount or when initialSessionId changes.
  // Three cases:
  //   1. Mount/change with a session ID → load it (unless we already have it with messages)
  //   2. Change to null/undefined → clear for new chat
  //   3. Same value, already loaded → skip
  const prevInitialSessionIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevInitialSessionIdRef.current;
    prevInitialSessionIdRef.current = initialSessionId;

    if (initialSessionId) {
      // Skip if we already have this session loaded AND have messages
      if (sessionIdRef.current === initialSessionId && messages.length > 0) return;
      // Skip if the value hasn't changed (not a mount or prop update)
      if (initialSessionId === prev && sessionIdRef.current === initialSessionId) return;

      loadSession(initialSessionId);
    } else if (prev) {
      // Had a session, now cleared → new chat
      clearMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSessionId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  return {
    messages,
    isLoading,
    chatState,
    error,
    sessionId,
    isArchived,
    activeSkills,
    connectorStatuses,
    pendingQuestion,
    sendMessage,
    cancelRequest,
    clearMessages,
    loadSession,
    handleQuestionComplete,
    handleQuestionSkip,
    handleQuestionDismiss,
    approveToolCall,
    rejectToolCall,
  };
}
